import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { TextField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  ConfirmModal,
  FormActions,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateEnvironmentMutation,
  useDeleteEnvironmentMutation,
  useRenameEnvironmentMutation,
} from '~/modules/query/featureFlags';

import { ENV_KEY_RE } from '~/components/Constants/featureFlagsConstants';
import { errorText, isStale } from '~/components/pages/feature-flags/common';
import type { Environment } from '~/components/pages/feature-flags/types';
import { useReference } from '~/components/pages/feature-flags/useReference';
import { routes } from '~/app/routes';

const FORM_ID = 'environment-form';
const LIST = routes.featureFlagsAdmin('environments');

/** Create (`/feature-flags/admin/environments/new`) and rename (`/feature-flags/admin/environments/:id`) one environment. The subtree is behind the feature flags access gate (FeatureFlag purview). */
export default function EnvironmentPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const ref = useReference();
  const { environments } = ref.reference;
  const record = environments.find((e) => e.key === id);
  const notFound = ref.loaded && !isNew && !record && !ref.reference.pending;

  // The API exposes no version: the name stands in, so a rename by someone else reseeds the form.
  const { draft, patch, dirty, reset, markSaved } = useRecordDraft<Environment | null, Environment>(
    ref.loaded ? (isNew ? null : record) : undefined,
    {
      id: id ?? 'new',
      version: record?.name,
      toDraft: (e) => ({ key: e?.key ?? '', name: e?.name ?? '' }),
    },
  );
  const guard = useUnsavedGuard(dirty);

  const [deleting, setDeleting] = useState(false);
  const [createEnvironment] = useCreateEnvironmentMutation();
  const [renameEnvironment] = useRenameEnvironmentMutation();
  const [deleteEnvironment] = useDeleteEnvironmentMutation();

  const schema = z.object({
    name: z.string().trim().min(1, 'Enter a name.'),
    key: isNew
      ? z
          .string()
          .trim()
          .regex(ENV_KEY_RE, 'Use lowercase letters, numbers and dashes, starting with a letter.')
      : z.string(),
  });
  const check = useFormValidation(schema, draft, (field) => `environment-${field}`);

  const save = useRecordSave<Environment>({
    save: (d) =>
      isNew
        ? createEnvironment({ key: d.key.trim(), name: d.name.trim() }).unwrap()
        : renameEnvironment({
            key: d.key,
            data: { name: d.name.trim(), expectedName: record?.name ?? '' },
          }).unwrap(),
    success: isNew ? 'Environment created' : 'Environment renamed',
    failure: isNew
      ? 'The environment could not be created. Try again.'
      : 'The environment could not be renamed. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(routes.featureFlagsAdminRecord('environments', draft.key.trim()), { replace: true });
  };

  const remove = () => {
    if (!record) return;
    deleteEnvironment({
      key: record.key,
      data: { expectedName: record.name, confirmation: record.key },
    })
      .unwrap()
      .then(() => {
        toast.success('Environment deleted');
        guard.release();
        navigate(LIST, { replace: true });
      })
      .catch((err) =>
        toast.error(
          isStale(err)
            ? 'This environment was renamed by someone else. Reload and try again.'
            : errorText(err, 'The environment could not be deleted. Try again.'),
        ),
      );
    setDeleting(false);
  };

  const title = isNew ? 'Create environment' : (record?.name ?? 'Environment');
  const failed = !ref.loaded && !!ref.error;
  const isLast = environments.length <= 1;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Admin' },
        { label: 'Environments', href: LIST },
        { label: title },
      ]}
      actions={
        record
          ? [
              {
                label: 'Delete environment',
                kind: 'danger',
                testId: 'environment-delete',
                startIcon: <Trash2 className="size-4" aria-hidden="true" />,
                disabled: isLast,
                onClick: () => setDeleting(true),
              },
            ]
          : undefined
      }
      loading={!draft}
      notFound={notFound}
      notFoundTitle="Environment not found"
      backTo={LIST}
      backLabel="Back to environments"
      error={failed ? { what: 'the environment', message: queryErrorMessage(ref.error) } : null}
      onRetry={ref.refetch}
      guard={guard}
      testId="environment"
    >
      {draft && (
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <Card>
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              <CardSection
                title="Details"
                description={
                  isNew
                    ? 'New environments start with every feature flag disabled. Copy settings from another environment in the flag editor.'
                    : isLast
                      ? 'Keep at least one environment, so this one cannot be deleted.'
                      : undefined
                }
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <TextField
                    id="environment-name"
                    testId="environment-name"
                    label="Name"
                    required
                    value={draft.name}
                    onChange={(name) => patch({ name })}
                    onBlur={() => check.touch('name')}
                    error={check.errors.name}
                    placeholder="Staging"
                  />
                  <TextField
                    id="environment-key"
                    testId="environment-key"
                    label="Environment key"
                    required
                    code
                    readOnly={!isNew}
                    value={draft.key}
                    onChange={(key) => patch({ key: key.toLowerCase() })}
                    onBlur={() => check.touch('key')}
                    error={check.errors.key}
                    placeholder="staging"
                    helperText={
                      isNew
                        ? 'Lowercase letters, numbers and dashes. Used by connected apps.'
                        : 'The key stays fixed so connected apps keep working.'
                    }
                  />
                </div>
              </CardSection>
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                {...(isNew
                  ? {
                      discardHref: LIST,
                      discardLabel: 'Cancel',
                      saveLabel: 'Create environment',
                      busyLabel: 'Creating…',
                    }
                  : {
                      onDiscard: () => {
                        reset();
                        check.reset();
                        save.clearError();
                      },
                      disabled: !dirty,
                      disabledReason: 'No changes to save',
                    })}
                busy={save.busy}
                saveTestId="environment-save"
              />
            </CardFooter>
          </Card>
        </form>
      )}

      <ConfirmModal
        isOpen={deleting}
        title="Delete environment"
        message={`Delete environment '${record?.name}'?`}
        confirmLabel="Delete environment"
        variant="danger"
        dangerNote="This permanently removes its flag settings, update settings, saved choices, users and SDK keys. Feature requests are kept. Apps using this environment stop receiving settings."
        confirmText={record?.key}
        testId="environment-delete-confirm"
        onClose={() => setDeleting(false)}
        onConfirm={remove}
      />
    </RecordPageShell>
  );
}
